import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { handleErrorLocallyHttpOptions } from '@app/shared/interceptors/handle-error-locally.context';
import { ModelWithContextData } from '@app/shared/services/dtos/dto-with-context-data.dto';
import { ModifyAbsenceReportModel } from '@app/shared/services/dtos/reports/modify-absence-report.model';
import { TimeReportDTO } from '@app/shared/services/dtos/reports/time-report.dto';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

/** Date range of a time-report read, as ISO instants. */
export interface TimeReportsRangeParams {
  dateFrom: string;
  dateTo: string;
}

/** Owner of a WorkTime report write: the task, and the user when written on their behalf. */
export interface WorkTimeReportTarget {
  taskId: string;
  userId?: string;
}

/** Owner of an Absence report write: the company, and the user when written on their behalf. */
export interface AbsenceReportTarget {
  companyId: string;
  userId?: string;
}

/** Body of one Absence write: the regular model, or the model with series occurrence fields. */
export type AbsenceReportWriteBody = ModifyAbsenceReportModel | Record<string, unknown>;

/**
 * TimeReportsHttpService - HTTP calls of the time-report endpoints: mixed and task reads, WorkTime and
 * Absence writes, deletion and approval.
 *
 * Write responses are returned untyped (`unknown`), because the API answers them in several shapes;
 * the caller normalizes them.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Used only by {@link TimeReportsDataService}, which maps responses, caches them and reports feedback.
 */
@Injectable({ providedIn: 'root' })
export class TimeReportsHttpService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.APIEndPoint;

  public getTimeReports(params: TimeReportsRangeParams): Observable<ModelWithContextData<TimeReportDTO[]>> {
    return this.httpClient.get<ModelWithContextData<TimeReportDTO[]>>(`${this.baseUrl}time-reports`, {
      params: { ...params }
    });
  }

  public getTaskWorkTimeReports(taskId: string, params: TimeReportsRangeParams): Observable<TimeReportDTO[]> {
    return this.httpClient.get<TimeReportDTO[]>(`${this.baseUrl}tasks/${taskId}/work-time-reports`, {
      params: { ...params }
    });
  }

  /** @param handleErrorLocally `true` skips the global error toast; the caller reports the failure. */
  public createWorkTimeReport(
    target: WorkTimeReportTarget,
    body: FormData,
    handleErrorLocally = false
  ): Observable<unknown> {
    const userPath = target.userId ? `/users/${target.userId}` : '';
    return this.httpClient.post<unknown>(
      `${this.baseUrl}tasks/${target.taskId}${userPath}/work-time-reports`,
      body,
      handleErrorLocallyHttpOptions(handleErrorLocally)
    );
  }

  /** @param handleErrorLocally `true` skips the global error toast; the caller reports the failure. */
  public createAbsenceReport(
    target: AbsenceReportTarget,
    body: AbsenceReportWriteBody,
    handleErrorLocally = false
  ): Observable<unknown> {
    const userPath = target.userId ? `/users/${target.userId}` : '';
    return this.httpClient.post<unknown>(
      `${this.baseUrl}companies/${target.companyId}${userPath}/absence-reports`,
      body,
      handleErrorLocallyHttpOptions(handleErrorLocally)
    );
  }

  public updateWorkTimeReport(reportId: string, body: FormData): Observable<unknown> {
    return this.httpClient.put<unknown>(`${this.baseUrl}work-time-reports/${reportId}`, body);
  }

  public updateAbsenceReport(reportId: string, body: ModifyAbsenceReportModel): Observable<unknown> {
    return this.httpClient.put<unknown>(`${this.baseUrl}absence-reports/${reportId}`, body);
  }

  public deleteTimeReport(reportId: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.baseUrl}time-reports/${reportId}`);
  }

  /** Errors are left to the caller, which reports the outcome of the whole approval batch. */
  public approveTimeReport(reportId: string): Observable<void> {
    return this.httpClient.put<void>(
      `${this.baseUrl}time-reports/${reportId}/approve`,
      {},
      handleErrorLocallyHttpOptions(true)
    );
  }
}
